import React, { useCallback } from 'react';
import cl from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { SelectedMaterialsSidebarFooterMaterialsSet } from './SelectedMaterialsSidebarFooter.types';

import {
  MaterialsSetSelectedMaterialsID,
  UpdateMaterialsSetCacheAction
} from '../../../../../../materialsSets/materialsSetsTypes';

import { MaterialsListOnMaterialsSelect } from '../../../../../../materials/components/lists/MaterialsList';

import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';

import { DownloadMaterialsSetButton } from '../../../../../../materialsSets/components/buttons/DownloadMaterialsSetButton';
import { DownloadMaterialsSetSeparatelyButton } from '../../../../../../materialsSets/components/buttons/DownloadMaterialsSetSeparatelyButton';
import { SelectedMaterialsSidebarActionsPopover } from '../SelectedMaterialsSidebarActionsPopover';
import { SelectedMaterialsSidebarAttachModalButton } from '../SelectedMaterialsSidebarAttachModalButton';

import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';

import { words } from '../../../../../../../locales/keys';

import { MaterialsSetsPermissions } from '../../../../../../materialsSets/materialsSetsConstants';

const selectedMaterialsSidebarActionsPermissions = [
  MaterialsSetsPermissions.READ_MATERIALS_SET_CLEAR_BUTTON
];

interface SelectedMaterialsSidebarFooterProps {
  materialsSet: SelectedMaterialsSidebarFooterMaterialsSet;
  checkedSelectedMaterialIds: MaterialsSetSelectedMaterialsID[];
  onCustomAttach?: MaterialsListOnMaterialsSelect;
  onClose: () => void;
  updateMaterialsSetCache: UpdateMaterialsSetCacheAction<SelectedMaterialsSidebarFooterMaterialsSet>;
  disabledAttach?: boolean;
}

function SelectedMaterialsSidebarFooter({
  materialsSet,
  checkedSelectedMaterialIds,
  onCustomAttach,
  onClose,
  updateMaterialsSetCache,
  disabledAttach
}: SelectedMaterialsSidebarFooterProps) {
  const currentUser = useCurrentUser();

  const handleCustomAttach = useCallback(() => {
    if (onCustomAttach && materialsSet) {
      const checkedMaterials = materialsSet.selectedMaterials?.filter(
        (material) => checkedSelectedMaterialIds?.includes(material.id)
      );

      onCustomAttach(checkedMaterials);
    }
  }, [checkedSelectedMaterialIds, materialsSet, onCustomAttach]);

  const withActionsPopover = selectedMaterialsSidebarActionsPermissions.some(
    (action) => currentUser.hasPermissions(action)
  );

  return (
    <div className="justify-between flex space-x-2">
      <CheckPermissions
        action={MaterialsSetsPermissions.READ_MATERIALS_SET_DOWNLOAD_BUTTON}
      >
        <DownloadMaterialsSetButton
          className="font-medium hover:bg-gray-200 items-center px-4 py-2 rounded-md text-gray-700 text-sm dark:hover:bg-gray-700 dark:text-gray-300 focus:ring-offset-0"
          materialsSetUuid={materialsSet.uuid}
        />
      </CheckPermissions>

      <CheckPermissions
        action={
          MaterialsSetsPermissions.READ_MATERIALS_SET_DOWNLOAD_SEPARATELY_BUTTON
        }
      >
        <DownloadMaterialsSetSeparatelyButton
          className="font-medium hover:bg-gray-200 items-center px-4 py-2 rounded-md text-gray-700 text-sm dark:hover:bg-gray-700 dark:text-gray-300 focus:ring-offset-0"
          dataGa="download-materials-set-separately-button"
          i18nText={words.download}
          materialsSetUuid={materialsSet.uuid}
        />
      </CheckPermissions>

      <span className="grow relative z-0 inline-flex justify-end shadow-sm rounded-md">
        {onCustomAttach ? (
          <PureButtonHelper
            className={cl(
              'relative inline-flex items-center px-4 py-2 rounded-l-md bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 focus:z-10',
              withActionsPopover ? 'rounded-l-md' : 'rounded-md'
            )}
            dataGa="selected-materials-set-attach-button"
            i18nText={words.attach}
            onClick={handleCustomAttach}
            disabled={isEmpty(checkedSelectedMaterialIds) || disabledAttach}
          />
        ) : (
          <SelectedMaterialsSidebarAttachModalButton
            optimisticUpdate={materialsSet.optimisticUpdate}
            selectedMaterialIds={checkedSelectedMaterialIds}
            onClose={onClose}
            updateMaterialsSetCache={updateMaterialsSetCache}
            className={cl(
              'relative inline-flex items-center px-4 py-2 rounded-l-md bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 focus:z-10',
              withActionsPopover ? 'rounded-l-md' : 'rounded-md'
            )}
            disabled={isEmpty(checkedSelectedMaterialIds) || disabledAttach}
          />
        )}

        {withActionsPopover && (
          <SelectedMaterialsSidebarActionsPopover
            materialsSet={materialsSet}
            onClose={onClose}
            updateMaterialsSetCache={updateMaterialsSetCache}
          />
        )}
      </span>
    </div>
  );
}

export default SelectedMaterialsSidebarFooter;
